import { useId } from "react";
import {
  useFormContext,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { FormControl } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import { TextField } from "@mui/material";
import { useTranslations } from "next-intl";
import { getValidationRules } from "./utils/getValidationRules";
import { HelperMessage } from "./HelperMessage";
import { FormControlLabel } from "./FormControlLabel";

interface FormTextAreaProps extends Omit<TextFieldProps, "variant"> {
  className?: string;
  name: string;
  label?: string;
  required?: boolean;
  rules?: RegisterOptions;
  resize?: boolean;
  showError?: boolean;
  fullHeight?: boolean;
}

export const FormTextArea: React.FC<FormTextAreaProps> = ({
  className,
  name,
  label = "",
  required = true,
  rules = {},
  placeholder,
  maxRows = 3,
  minRows = 1,
  showError,
  fullHeight,
  ...rest
}) => {
  const id = useId();
  const t = useTranslations();
  const style = fullHeight ? { height: "100%" } : undefined;

  const validationRules = getValidationRules({
    required,
    rules,
    t,
  });

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    field: { onChange, onBlur, value },
  } = useController({
    name,
    rules: validationRules,
    control,
  });

  return (
    <FormControl className={className ?? "body-l flex flex-1 flex-col"}>
      <FormControlLabel id={id} label={label} />
      <TextField
        id={id}
        maxRows={maxRows}
        minRows={minRows}
        multiline
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          ...style,
        }}
        value={value as string}
        {...rest}
      />

      <HelperMessage errors={errors} name={name} showError={showError} />
    </FormControl>
  );
};
